import { colourDistanceSquared, type Rgba } from './colour';

/**
 * Scanline flood fill over a raw RGBA buffer.
 *
 * Deliberately allocation-light and iterative: a naive recursive fill overflows
 * the stack on a 4096×4096 layer, and a per-pixel queue allocates a record per
 * pixel. Spans are pushed as single integers, so a large region stays cheap.
 *
 * The tolerance compares RGBA distance, so a transparent region is a region:
 * clicking an empty layer fills the whole empty area rather than nothing.
 */

export interface FloodFillRequest {
  data: Uint8ClampedArray;
  width: number;
  height: number;
  x: number;
  y: number;
  /** Replacement colour, already premultiplied by nothing — plain RGBA. */
  colour: Rgba;
  /** Squared distance from `colour.ts`; 0 means exact match. */
  toleranceSquared: number;
}

export type FloodFillOutcome =
  | { filled: number }
  | { filled: 0; reason: 'outside' | 'already-that-colour' };

export function floodFillPixels(request: FloodFillRequest): FloodFillOutcome {
  const { data, width, height, colour, toleranceSquared } = request;
  const x = Math.floor(request.x);
  const y = Math.floor(request.y);
  if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x >= width || y >= height) {
    return { filled: 0, reason: 'outside' };
  }

  const at = (px: number, py: number): number => (py * width + px) * 4;
  const matches = (px: number, py: number): boolean => {
    const i = at(px, py);
    return (
      colourDistanceSquared(
        {
          red: data[i] ?? 0,
          green: data[i + 1] ?? 0,
          blue: data[i + 2] ?? 0,
          alpha: data[i + 3] ?? 0,
        },
        target,
      ) <= toleranceSquared
    );
  };

  const i0 = at(x, y);
  const target: Rgba = {
    red: data[i0] ?? 0,
    green: data[i0 + 1] ?? 0,
    blue: data[i0 + 2] ?? 0,
    alpha: data[i0 + 3] ?? 0,
  };
  if (colourDistanceSquared(target, colour) <= toleranceSquared) {
    return { filled: 0, reason: 'already-that-colour' };
  }

  const visited = new Uint8Array(width * height);
  const stack: number[] = [y * width + x];
  let filled = 0;

  while (stack.length > 0) {
    const seed = stack.pop();
    if (seed === undefined) break;
    if (visited[seed]) continue;
    const px = seed % width;
    const py = Math.floor(seed / width);
    if (!matches(px, py)) continue;

    let left = px;
    let right = px;
    while (left > 0 && !visited[py * width + left - 1] && matches(left - 1, py)) left -= 1;
    while (right < width - 1 && !visited[py * width + right + 1] && matches(right + 1, py))
      right += 1;

    for (let cx = left; cx <= right; cx += 1) {
      const index = py * width + cx;
      visited[index] = 1;
      const i = index * 4;
      data[i] = colour.red;
      data[i + 1] = colour.green;
      data[i + 2] = colour.blue;
      data[i + 3] = colour.alpha;
      filled += 1;

      if (py > 0 && !visited[index - width] && matches(cx, py - 1)) stack.push(index - width);
      if (py < height - 1 && !visited[index + width] && matches(cx, py + 1))
        stack.push(index + width);
    }
  }

  return { filled };
}
